import { MeetingProvider } from 'api/meeting/types';
import { useGetMeetingSettings } from 'queries/meetings';
import { AIFill } from 'vendor/ckeditor/plugins/ai-fill/plugin';
import { useIsAiFillsBetaEnabled } from './ai-fills/use-ai-fills-enabled';
import { AccountFeature, useAccountFeature } from './use-account-feature';
import { useIsNativeUser } from './use-user-type';

/**
 *
 * @description Use this hook to get AI Fill related plugins, and this hook restricts access based on common conditions. The FE feature flag is done at
 * plugin level, please remove it from `AiFill` and `useFeature` after release.
 */
export const useGetAiFillsPlugins = (check?: (() => boolean) | boolean) => {
  const isNativeUser = useIsNativeUser();
  const { data: isBetaEnabled } = useIsAiFillsBetaEnabled();
  const [isAiFillsEnabled] = useAccountFeature(AccountFeature.AiFills);
  const { data: aiFillSettings } = useGetMeetingSettings({
    enabled: isAiFillsEnabled,
  });

  const checkResult =
    check !== undefined
      ? typeof check === 'function'
        ? check()
        : !!check
      : true;

  const isAiFillEnabledAndConnected =
    isAiFillsEnabled && aiFillSettings !== undefined
      ? aiFillSettings.gongEnabled
        ? aiFillSettings.recordingMetaByProvider?.[MeetingProvider.Gong]
            .isConnected
        : [
            MeetingProvider.Zoom,
            MeetingProvider.GoogleMeet,
            MeetingProvider.MicrosoftTeams,
          ].some(
            (provider) =>
              aiFillSettings.recordingMetaByProvider?.[provider].isConnected,
          )
      : false;

  return isNativeUser &&
    isBetaEnabled &&
    isAiFillsEnabled &&
    isAiFillEnabledAndConnected &&
    checkResult
    ? [AIFill]
    : [];
};

export const useIsAiFillsEnabled = (check?: (() => boolean) | boolean) => {
  const isNativeUser = useIsNativeUser();
  const { data: isBetaEnabled, isLoading: isLoadingFlag } =
    useIsAiFillsBetaEnabled();
  const { data: aiFillSettings, isLoading } = useGetMeetingSettings({
    enabled: isBetaEnabled,
  });
  const [isAiFillsEnabled] = useAccountFeature(AccountFeature.AiFills);

  const checkResult =
    check !== undefined
      ? typeof check === 'function'
        ? check()
        : !!check
      : true;

  const isAiFillEnabledAndConnected =
    isAiFillsEnabled && aiFillSettings !== undefined
      ? aiFillSettings.gongEnabled
        ? aiFillSettings.recordingMetaByProvider?.[MeetingProvider.Gong]
            .isConnected
        : [
            MeetingProvider.Zoom,
            MeetingProvider.GoogleMeet,
            MeetingProvider.MicrosoftTeams,
          ].some(
            (provider) =>
              aiFillSettings.recordingMetaByProvider?.[provider].isConnected,
          )
      : false;

  return {
    data: !!(
      isNativeUser &&
      isBetaEnabled &&
      isAiFillsEnabled &&
      isAiFillEnabledAndConnected &&
      checkResult
    ),
    isLoading: isLoadingFlag || isLoading,
  };
};
